<template>
  <m-card-item
    icon="download"
    @click="showDialog = true"
  >
    {{ $t('exportMenu.export') }}
  </m-card-item>
  <m-dialog
    v-model:value="showDialog"
    :title="$t('exportMenu.exportFormat')"
    hide-header
    :ok-text="$t('exportMenu.export')"
    :max-width="$modalSizes.sm"
    @ok="handleExport"
    @cancel="showDialog = false"
  >
    <div class="_export-format-select">
      <div class="_export-format-select-title">
        {{ $t('exportMenu.selectExportFormat') }}
      </div>
      <m-select
        v-model:value="selectedFormat"
        :items="exportFormatOptions"
      />
    </div>
  </m-dialog>
</template>

<script setup>
import useOverlaySpinner from '@/composables/overlay-spinner';
import usePageExport from '@/composables/export/page-export';
import useSnackBar from '@/composables/snackbar';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { computed, ref } from 'vue';
import { doCsvExport } from '@/api';
import { downloadFromBlob } from 'shared/lib/file-download';
import { logCatch } from '@/lib/logger/logger';
import { reportExportFormat } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackBar = useSnackBar();
const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();

const props = defineProps({
  // only pdf/png related props:
  page: {
    type: String,
    default: undefined,
  },
  gridPageId: {
    type: Number,
    default: undefined,
  },
  // both pdf/png and csv related props:
  allowedExportFormats: {
    type: Array,
    required: true,
  },
  savedView: {
    type: Object,
    default: undefined,
  },
});

const emit = defineEmits(['export-requested']);

const { exportPage } = usePageExport();

const showDialog = ref(false);

const selectedFormat = ref(props.allowedExportFormats[0]);
const exportFormatOptions = computed(() => props.allowedExportFormats.map((format) => ({
  text: t(`exportPage.exportFormat.${format}`),
  value: format,
})));

const handleExport = () => {
  showOverlaySpinner(t('general.exporting'));

  const exportHandlers = {
    [reportExportFormat.pdf]: exportPdfPng,
    [reportExportFormat.image]: exportPdfPng,
    [reportExportFormat.csv]: exportCsv,
  };

  const exportFn = exportHandlers[selectedFormat.value];
  if (!exportFn) {
    hideOverlaySpinner();
    throw new Error(`Unsupported export format: ${selectedFormat.value}`);
  }

  exportFn().finally(() => {
    hideOverlaySpinner();
  });

  showDialog.value = false;
  emit('export-requested');
};

const exportPdfPng = async () => {
  if (!props.page) {
    throw new Error('PDF/PNG page is required');
  }

  const printableData = {
    page: props.page,
    exportFormat: selectedFormat.value,
  };

  if (props.gridPageId) {
    printableData.gridPage = { uid: props.gridPageId };
  }

  if (props.savedView && props.savedView.uid) {
    printableData.savedView = { uid: props.savedView.uid };
  }

  await exportPage(printableData);
};

const exportCsv = async () => {
  if (!props.savedView) {
    throw new Error('CSV saved view is required');
  }

  let filter = null;
  if (props.savedView !== null && props.savedView.params !== null) {
    filter = props.savedView.params.filter;
  }
  const handler = new GoalFilterHandler();
  const filterObject = handler.Translate(filter);

  try {
    const response = await doCsvExport({ filter: filterObject });
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'mooncamp_export.csv'; // Fallback filename
    if (contentDisposition && contentDisposition.includes('filename=')) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }
    downloadFromBlob(filename, new Blob([response.data]));
  } catch (e) {
    logCatch((err) => {
      if (err.status === 429) {
        snackBar.error(t('exportButton.tooManyRequests'));
        return;
      }
      snackBar.error();
    })(e);
  }
};
</script>

<style scoped lang="scss">
._export-format-select {
  display: flex;
  flex-direction: row;
  align-items: center;

  ._export-format-select-title {
    margin-right: auto;
    color: $font-color-secondary;
  }
}
</style>
